import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ShortTextIcon from "@mui/icons-material/ShortText";
import NotesIcon from "@mui/icons-material/Notes";

const QUESTION_TYPES = [
  { value: "SINGLE_CHOICE", label: "Respuesta única" },
  { value: "MULTIPLE_CHOICE", label: "Varias respuestas" },
  { value: "SHORT_TEXT", label: "Respuesta corta" },
  { value: "PARAGRAPH", label: "Párrafo" },
];

const OPTION_LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");
export const EVALUATION_TOTAL_POINTS = 10;

export function getQuestionPoints(question) {
  const points = Number(question?.points ?? 0);
  return Number.isFinite(points) ? points : 0;
}

export function getQuestionsTotalPoints(questions = []) {
  return questions.reduce((sum, question) => sum + getQuestionPoints(question), 0);
}

export function getRemainingPointsForNewQuestion(questions = []) {
  const remaining = EVALUATION_TOTAL_POINTS - getQuestionsTotalPoints(questions);
  return Number(Math.max(0, remaining).toFixed(2));
}

function createLocalId() {
  const cryptoObj = typeof window !== "undefined" ? window.crypto : null;

  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }

  return `question-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function defaultOptions() {
  return [
    { id: "a", text: "" },
    { id: "b", text: "" },
  ];
}

export function makeQuestion(overrides = {}) {
  return {
    localId: createLocalId(),
    question: "",
    questionType: "SINGLE_CHOICE",
    points: EVALUATION_TOTAL_POINTS,
    options: defaultOptions(),
    correct: ["a"],
    ...overrides,
  };
}

export function normalizeQuestionFromApi(question) {
  const isSelection =
    question.questionType === "SINGLE_CHOICE" ||
    question.questionType === "MULTIPLE_CHOICE";

  return makeQuestion({
    id: question.id,
    question: question.question ?? "",
    questionType: question.questionType ?? "SINGLE_CHOICE",
    points: Number(question.points ?? 0),
    options:
      isSelection && Array.isArray(question.options) && question.options.length
        ? question.options
        : defaultOptions(),
    correct:
      isSelection && Array.isArray(question.correct)
        ? question.correct
        : isSelection
        ? ["a"]
        : [],
  });
}

function isSelectionType(type) {
  return type === "SINGLE_CHOICE" || type === "MULTIPLE_CHOICE";
}

function getQuestionTypeLabel(type) {
  return QUESTION_TYPES.find((item) => item.value === type)?.label || type;
}

function InsertQuestionHoverZone({ onClick }) {
  return (
    <Box
      sx={{
        position: "relative",
        height: 20,
        my: 0.2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover .insert-question-button": {
          opacity: 1,
          transform: "translateY(0)",
          pointerEvents: "auto",
        },
      }}
    >
      <Button
        className="insert-question-button"
        onClick={onClick}
        sx={{
          position: "absolute",
          top: "0%",
          left: 0,
          right: 0,
          transform: "translateY(-50%) scale(0.98)",
          height: 20,
          opacity: 0,
          pointerEvents: "none",
          borderRadius: "8px",
          border: "1.5px solid rgba(0,0,0,0.45)",
          bgcolor: "white",
          color: "neutral.700",
          textTransform: "none",
          fontSize: 12,
          fontWeight: 800,
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          transition: "all 150ms ease",
          "&:hover": {
            bgcolor: "secondary.50",
            borderColor: "primary.600",
          },
        }}
      >
        + Añadir pregunta
      </Button>
    </Box>
  );
}

function FinalAddQuestionButton({ onClick }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        width: "100%",
        height: 32,
        mt: 0.5,
        borderRadius: "8px",
        border: "1.5px solid rgba(0,0,0,0.45)",
        bgcolor: "white",
        color: "neutral.700",
        textTransform: "none",
        fontSize: 13,
        fontWeight: 800,
        boxShadow: "none",
        "&:hover": {
          bgcolor: "secondary.50",
          borderColor: "primary.600",
        },
      }}
    >
      + Añadir pregunta
    </Button>
  );
}

function SelectionOptionRow({
  question,
  option,
  onToggleCorrect,
  onOptionTextChange,
  onDeleteOption,
}) {
  const checked = question.correct.includes(option.id);
  const isSingle = question.questionType === "SINGLE_CHOICE";

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: "center",
        minHeight: 30,
        "&:hover .delete-option": {
          opacity: 1,
        },
      }}
    >
      <Checkbox
        checked={checked}
        onChange={() => onToggleCorrect(option.id)}
        icon={
          isSingle ? (
            <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />
          ) : (
            <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />
          )
        }
        checkedIcon={
          isSingle ? (
            <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
          ) : (
            <CheckBoxIcon sx={{ fontSize: 18 }} />
          )
        }
        sx={{
          p: 0.3,
          color: "neutral.500",
          "&.Mui-checked": { color: "primary.700" },
        }}
      />

      <TextField
        variant="standard"
        placeholder={`Opción ${option.id.toUpperCase()}`}
        value={option.text}
        onChange={(event) => onOptionTextChange(option.id, event.target.value)}
        size="small"
        sx={{
          width: "min(320px, 100%)",
          "& .MuiInputBase-input": {
            fontSize: 12,
            fontWeight: 700,
            color: "primary.900",
            py: 0.2,
          },
          "& .MuiInput-underline:before": {
            borderBottomColor: "rgba(0,0,0,0.25)",
          },
          "& .MuiInput-underline:hover:before": {
            borderBottomColor: "primary.500",
          },
        }}
      />

      {question.options.length > 2 && (
        <Tooltip title="Eliminar opción">
          <IconButton
            className="delete-option"
            size="small"
            onClick={() => onDeleteOption(option.id)}
            sx={{
              opacity: 0,
              transition: "opacity 140ms ease",
              width: 24,
              height: 24,
              bgcolor: "neutral.100",
              "&:hover": { bgcolor: "#ffcdd2" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15, color: "#d32f2f" }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function TextAnswerPreview({ type }) {
  const isParagraph = type === "PARAGRAPH";

  return (
    <Stack spacing={0.8} sx={{ mt: 1.5, maxWidth: isParagraph ? 420 : 330 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {isParagraph ? (
          <NotesIcon sx={{ fontSize: 17, color: "neutral.600" }} />
        ) : (
          <ShortTextIcon sx={{ fontSize: 17, color: "neutral.600" }} />
        )}

        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "neutral.600" }}>
          {isParagraph
            ? "El evaluado deberá ingresar una respuesta larga."
            : "El evaluado deberá ingresar una respuesta no más larga de 50 caracteres."}
        </Typography>
      </Stack>

      <Box
        sx={{
          height: isParagraph ? 70 : 28,
          border: "1px solid rgba(0,0,0,0.18)" ,
          borderRadius: "12px",
          px: isParagraph ? 1.3 : 1.3,
          py: isParagraph ? 1 : 0.8,
          color: "neutral.500",
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {isParagraph ? "Texto de párrafo" : "Texto de respuesta corta"}
      </Box>
    </Stack>
  );
}

function QuestionBlock({
  question,
  index,
  canDelete,
  onChangeQuestion,
  onDeleteQuestion,
  onAddOption,
  onDeleteOption,
  onOptionTextChange,
  onToggleCorrect,
}) {
  const isSelection = isSelectionType(question.questionType);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: "22px",
        bgcolor: "white",
        border: "1.7px solid rgba(0,0,0,0.42)",
        boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        "&:hover": {
          borderColor: "primary.500",
          boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Stack spacing={1.2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ alignItems: { xs: "stretch", md: "flex-start" } }}
        >
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              variant="standard"
              placeholder="Pregunta"
              value={question.question}
              onChange={(event) =>
                onChangeQuestion({
                  ...question,
                  question: event.target.value,
                })
              }
              fullWidth
              sx={{
                maxWidth: 420,
                "& .MuiInputBase-input": {
                  fontSize: 18,
                  fontWeight: 700,
                  color: "primary.900",
                  py: 0.2,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "rgba(0,0,0,0.35)",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottomColor: "primary.600",
                },
              }}
            />

            {isSelection ? (
              <Stack spacing={0.5}>
                {question.options.map((option) => (
                  <SelectionOptionRow
                    key={option.id}
                    question={question}
                    option={option}
                    onToggleCorrect={onToggleCorrect}
                    onOptionTextChange={onOptionTextChange}
                    onDeleteOption={onDeleteOption}
                  />
                ))}

                <Button
                  onClick={onAddOption}
                  startIcon={<AddIcon />}
                  sx={{
                    alignSelf: "flex-start",
                    mt: 0.3,
                    ml: 0.1,
                    px: 0.5,
                    color: "neutral.500",
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 800,
                    "&:hover": {
                      bgcolor: "transparent",
                      color: "primary.700",
                    },
                  }}
                >
                  Añadir opción
                </Button>
              </Stack>
            ) : (
              <TextAnswerPreview type={question.questionType} />
            )}
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "flex-start",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              minWidth: { md: 270 },
            }}
          >
            {canDelete && (
              <Tooltip title="Eliminar pregunta">
                <IconButton
                  size="small"
                  onClick={onDeleteQuestion}
                  sx={{
                    width: 30,
                    height: 30,
                    border: "1px solid #ff6b6b",
                    bgcolor: "rgba(255,107,107,0.08)",
                    "&:hover": { bgcolor: "rgba(255,107,107,0.18)" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18, color: "#ff5252" }} />
                </IconButton>
              </Tooltip>
            )}

            <TextField
              label="Puntaje"
              type="number"
              size="small"
              value={question.points ?? 1}
              onChange={(event) =>
                onChangeQuestion({
                  ...question,
                  points: event.target.value,
                })
              }
              inputProps={{ min: 0, step: 0.25 }}
              sx={{
                width: 100,
                bgcolor: "white",
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  borderRadius: "9px",
                  fontSize: 13,
                  fontWeight: 800,
                  color: "primary.900",
                },
              }}
            />

            <TextField
              select
              size="small"
              value={question.questionType}
              onChange={(event) => {
                const nextType = event.target.value;
                const nextIsSelection = isSelectionType(nextType);

                const nextOptions =
                  nextIsSelection && question.options?.length >= 2
                    ? question.options
                    : nextIsSelection
                    ? defaultOptions()
                    : [];

                const availableOptionIds = new Set(nextOptions.map((option) => option.id));
                const currentCorrect = (question.correct || []).filter((id) =>
                  availableOptionIds.has(id)
                );

                onChangeQuestion({
                  ...question,
                  questionType: nextType,
                  options: nextOptions,
                  correct: nextIsSelection
                    ? currentCorrect.length
                      ? currentCorrect
                      : [nextOptions[0]?.id].filter(Boolean)
                    : [],
                });
              }}
              SelectProps={{
                renderValue: (value) => getQuestionTypeLabel(value),
              }}
              sx={{
                width: 210,
                bgcolor: "white",
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  borderRadius: "9px",
                  fontSize: 13,
                  fontWeight: 800,
                  color: "primary.900",
                },
              }}
            >
              {QUESTION_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>

        {isSelection && (
          <Typography
            sx={{
              alignSelf: { xs: "flex-start", md: "flex-end" },
              mr: { md: 4 },
              mt: 0.5,
              fontSize: 10,
              fontWeight: 700,
              color: "neutral.500",
            }}
          >
            {question.questionType === "MULTIPLE_CHOICE"
              ? "Marca las respuestas correctas"
              : "Marca la respuesta correcta"}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function EvaluacionBodyBuilder({ questions, setQuestions }) {
  const updateQuestion = (localId, nextQuestion) => {
    setQuestions((prev) =>
      prev.map((question) =>
        question.localId === localId ? nextQuestion : question
      )
    );
  };

  const insertQuestionAfter = (index) => {
    setQuestions((prev) => {
      const next = [...prev];
      next.splice(
        index + 1,
        0,
        makeQuestion({ points: getRemainingPointsForNewQuestion(prev) })
      );
      return next;
    });
  };

  const appendQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      makeQuestion({ points: getRemainingPointsForNewQuestion(prev) }),
    ]);
  };

  const deleteQuestion = (localId) => {
    setQuestions((prev) =>
      prev.length <= 1 ? prev : prev.filter((question) => question.localId !== localId)
    );
  };

  const addOption = (localId) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.localId !== localId) return question;

        const usedIds = new Set(question.options.map((option) => option.id));
        const nextId =
          OPTION_LETTERS.find((letter) => !usedIds.has(letter)) || String(Date.now());

        return {
          ...question,
          options: [...question.options, { id: nextId, text: "" }],
        };
      })
    );
  };

  const deleteOption = (localId, optionId) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.localId !== localId) return question;

        const nextOptions = question.options.filter((option) => option.id !== optionId);
        const nextCorrect = question.correct.filter((id) => id !== optionId);

        return {
          ...question,
          options: nextOptions,
          correct:
            nextCorrect.length > 0
              ? nextCorrect
              : nextOptions[0]
              ? [nextOptions[0].id]
              : [],
        };
      })
    );
  };

  const optionTextChange = (localId, optionId, text) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.localId !== localId) return question;

        return {
          ...question,
          options: question.options.map((option) =>
            option.id === optionId ? { ...option, text } : option
          ),
        };
      })
    );
  };

  const toggleCorrect = (localId, optionId) => {
    setQuestions((prev) =>
      prev.map((question) => {
        if (question.localId !== localId) return question;

        if (question.questionType === "SINGLE_CHOICE") {
          return {
            ...question,
            correct: [optionId],
          };
        }

        const alreadySelected = question.correct.includes(optionId);

        if (alreadySelected && question.correct.length <= 1) {
          return question;
        }

        return {
          ...question,
          correct: alreadySelected
            ? question.correct.filter((id) => id !== optionId)
            : [...question.correct, optionId],
        };
      })
    );
  };

  const totalPoints = getQuestionsTotalPoints(questions);
  const totalOk = Math.abs(totalPoints - EVALUATION_TOTAL_POINTS) <= 0.01;

  return (
    <Stack spacing={1.2} sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.900" }}>
          Cuerpo
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 900,
            color: totalOk ? "success.main" : "error.main",
          }}
        >
          Total: {totalPoints.toFixed(2)} / {EVALUATION_TOTAL_POINTS}
        </Typography>
      </Stack>

      <Stack spacing={0}>
        {questions.map((question, index) => (
          <Box key={question.localId}>
            <QuestionBlock
              question={question}
              index={index}
              canDelete={questions.length > 1}
              onChangeQuestion={(nextQuestion) => updateQuestion(question.localId, nextQuestion)}
              onDeleteQuestion={() => deleteQuestion(question.localId)}
              onAddOption={() => addOption(question.localId)}
              onDeleteOption={(optionId) => deleteOption(question.localId, optionId)}
              onOptionTextChange={(optionId, text) =>
                optionTextChange(question.localId, optionId, text)
              }
              onToggleCorrect={(optionId) => toggleCorrect(question.localId, optionId)}
            />

            {index < questions.length - 1 && (
              <InsertQuestionHoverZone onClick={() => insertQuestionAfter(index)} />
            )}
          </Box>
        ))}

        <FinalAddQuestionButton onClick={appendQuestion} />
      </Stack>
    </Stack>
  );
}